import { Fragment } from 'react';
import type { GameView, PublicStats, RoomView } from '@mt/protocol';
import { useApp } from '../net/client';
import { ACTION_TITLES, cr, crn, secondsLeft } from '../lib/format';
import { pct, signedCr, toneOf } from './kit';

/** Открытая статистика игрока на его плашке (если он разрешил её показывать в профиле). */
export function SeatStats({ stats }: { stats: PublicStats }) {
  return (
    <span className="seat-stats" title={`Статистика игрока: розыгрышей ${stats.games}, винрейт ${pct(stats.winRate)}, итог ${signedCr(stats.net)} кр.`}>
      <span>WR {pct(stats.winRate)}</span>
      <span className={toneOf(stats.net)}>{signedCr(stats.net)}</span>
    </span>
  );
}

/** Доля оставшегося времени хода, после которой полоса становится тревожной. */
const TURN_WARN_SHARE = 0.3;

/** Участники розыгрыша: видны действия и суммы, но не прогнозы. Одна строка на игрока. */
export function GameSeats({ game, now }: { game: GameView; now: number }) {
  const turnMs = useApp((s) => s.info?.turnMs ?? 12_000);
  return (
    <table className="seat-table">
      <thead>
        <tr>
          <th>Игрок</th>
          <th className="num" title="Ставка в текущем круге, кр.">
            Круг
          </th>
          <th className="num" title="Всего внесено в банк розыгрыша, кр.">
            Внесено
          </th>
          <th className="num" title="Доступный остаток, кр.">
            Остаток
          </th>
        </tr>
      </thead>
      <tbody>
        {game.seats.map((s) => {
          const turn = game.toActPlayerId === s.playerId;
          const left = turn ? secondsLeft(game.turnEndsAt, now) : null;
          let act: string;
          let tone = '';
          if (turn) {
            act = `ход · ${left} с`;
            tone = 'accent';
          } else if (s.status === 'folded') act = 'пас';
          else if (s.status === 'allin') {
            act = 'олл-ин';
            tone = 'accent';
          } else if (s.lastAction)
            act = `${ACTION_TITLES[s.lastAction.type]}${s.lastAction.amount && s.lastAction.type !== 'ante' ? ' ' + cr(s.lastAction.amount) : ''}${s.lastAction.auto ? ' · авто' : ''}`;
          else act = '';
          return (
            <Fragment key={s.playerId}>
              <tr className={`${s.status} ${turn ? 'turn' : ''} ${s.isMe ? 'me' : ''}`}>
                <td>
                  <div className="who-cell">
                    <span className="who-name">
                      {s.name}
                      {s.isMe && <em className="tag me">вы</em>}
                      {s.isBot && <em className="tag">бот</em>}
                      {!s.connected && <em className="tag warn">нет связи</em>}
                    </span>
                    <span className={`who-act ${tone}`}>{act}</span>
                  {s.stats && <SeatStats stats={s.stats} />}
                  </div>
                </td>
                <td className="num mono">{s.roundBet ? crn(s.roundBet) : '—'}</td>
                <td className="num mono">{crn(s.committed)}</td>
                <td className="num mono">{crn(s.stack)}</td>
              </tr>
              {turn && game.turnEndsAt !== null && <TurnProgress endsAt={game.turnEndsAt} turnMs={turnMs} now={now} me={s.isMe} />}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/** Строка таблицы с полосой времени хода — под строкой ходящего игрока, видна всем за столом. */
function TurnProgress(props: { endsAt: number; turnMs: number; now: number; me: boolean }) {
  return (
    <tr className="turn-progress" aria-hidden="true">
      <td colSpan={4}>
        <TurnBar {...props} />
      </td>
    </tr>
  );
}

/**
 * Полоса оставшегося времени хода. Время берётся с сервера (turnEndsAt по серверным часам),
 * полоса только отображает его; в последние 30 % — тревожный цвет.
 */
export function TurnBar({ endsAt, turnMs, now, me }: { endsAt: number; turnMs: number; now: number; me: boolean }) {
  const share = Math.min(1, Math.max(0, (endsAt - now) / turnMs));
  return (
    <div className={`turn-bar ${me ? 'me' : ''} ${share <= TURN_WARN_SHARE ? 'low' : ''}`} aria-hidden="true">
      <i style={{ transform: `scaleX(${share})` }} />
    </div>
  );
}

/** Места за столом между розыгрышами. */
export function RoomSeats({ room }: { room: RoomView }) {
  const empty = Math.max(0, room.maxPlayers - room.seats.length);
  return (
    <table className="seat-table">
      <thead>
        <tr>
          <th>Игрок</th>
          <th>Статус</th>
          <th className="num">Баланс</th>
        </tr>
      </thead>
      <tbody>
        {room.seats.map((s) => (
          <tr key={s.playerId} className={s.isMe ? 'me' : ''}>
            <td>
              <span className="who-name">
                {s.name}
                {s.isMe && <em className="tag me">вы</em>}
                {s.isBot && <em className="tag">бот</em>}
                {!s.connected && <em className="tag warn">нет связи</em>}
              </span>
              {s.stats && <SeatStats stats={s.stats} />}
            </td>
            <td>{s.inGame ? <span className="dim">в розыгрыше</span> : s.ready ? <span className="ok">готов</span> : <span className="dim">ожидает</span>}</td>
            <td className="num mono">{crn(s.balance)}</td>
          </tr>
        ))}
        {Array.from({ length: empty }, (_, i) => (
          <tr key={'e' + i} className="empty">
            <td colSpan={3}>свободно</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
